import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, serverTimestamp, setDoc, updateDoc, } from "firebase/firestore";
import { LayoutDashboard, ShoppingBag, Receipt, Wallet, Landmark, Store, Users, Shirt, MessageSquare,  Globe, LogOut, Menu, X, Bell, Search, RefreshCw, Package, Clock, AlertCircle, CreditCard, TrendingUp, ShieldCheck, } from "lucide-react";
import { auth, db } from "../../firebase";
const sections = [
    { label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
    { label: "All orders", icon: ShoppingBag, key: "orders", collection: "orders" },
    { label: "Payments", icon: Receipt, key: "payments", collection: "payments" },
    { label: "Settlements", icon: Landmark, key: "settlements", collection: "withdrawals" },
    { label: "Wallets", icon: Wallet, key: "wallets", collection: "wallets" },
    { label: "Shop owners", icon: Store, key: "shopOwners", collection: "shopOwnerApplications" },
    { label: "Resellers", icon: Users, key: "resellers", collection: "resellerApplications" },
    { label: "All sarees", icon: Shirt, key: "sarees", collection: "products" },
    { label: "Enquire / Support Questions", icon: MessageSquare, key: "enquiries", collection: "enquiries" },
    { label: "Landing page", icon: Globe, key: "website" },
];
const money = (value) => new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
}).format(Number(value) || 0);
const displayDate = (value) => {
    if (!value)
        return "—";
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime()))
        return "—";
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};
function valueOf(record, keys, fallback = "—") {
    for (const key of keys) {
        if (record?.[key] !== undefined && record?.[key] !== null && record[key] !== "") {
            return record[key];
        }
    }
    return fallback;
}
function StatCard({ icon: Icon, label, value, note, tone = "rose" }) {
    const tones = {
        rose: "bg-rose-50 text-rose-800 border-rose-700",
        red: "bg-red-50 text-red-700 border-red-500",
        gold: "bg-amber-50 text-amber-700 border-amber-500",
        green: "bg-emerald-50 text-emerald-700 border-emerald-500",
        blue: "bg-blue-50 text-blue-700 border-blue-600",
        purple: "bg-violet-50 text-violet-700 border-violet-600",
    };
    return (<div className={`rounded-xl border border-stone-200 border-b-[3px] ${tones[tone].split(" ").at(-1)} bg-white p-4`}>
      <div className="flex items-center gap-3">
        <div className={`shrink-0 rounded-xl p-3 ${tones[tone].split(" ").slice(0, 2).join(" ")}`}>
          <Icon size={21}/>
        </div>
        <div className="min-w-0">
          <p className="text-xs leading-5 text-stone-500">{label}</p>
          <p className="mt-0.5 break-words text-2xl font-bold text-rose-950">{value}</p>
          <p className="mt-1 text-xs leading-4 text-stone-500">{note}</p>
        </div>
      </div>
    </div>);
}
function Status({ value }) {
    const status = String(value || "Unknown").toLowerCase();
    let style = "bg-stone-100 text-stone-700";
    if (/deliver|active|approved|paid|answered|closed|live|complete/.test(status)) {
        style = "bg-emerald-100 text-emerald-800";
    }
    else if (/pending|placed|open|new|requested|processing|unpaid/.test(status)) {
        style = "bg-amber-100 text-amber-800";
    }
    else if (/shipped|packed|progress/.test(status)) {
        style = "bg-blue-100 text-blue-800";
    }
    else if (/reject|suspend|cancel|failed/.test(status)) {
        style = "bg-red-100 text-red-800";
    }
    return (<span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${style}`}>
      <span className="mr-1.5">●</span>
      {String(value || "Unknown")}
    </span>);
}
export default function AdminDashboard() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [active, setActive] = useState("dashboard");
    const [mobileOpen, setMobileOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [records, setRecords] = useState({});
    const [loadingCollections, setLoadingCollections] = useState(true);
    const [errors, setErrors] = useState({});
    const [authError, setAuthError] = useState("");
    const [signingOut, setSigningOut] = useState(false);
    const [settlementModal, setSettlementModal] = useState(null);
    const [utrNumber, setUtrNumber] = useState("");
    const [enquiryReplies, setEnquiryReplies] = useState({});
    const [savingEnquiryId, setSavingEnquiryId] = useState("");
    const [settlementSaving, setSettlementSaving] = useState(false);
    useEffect(() => {
        let unsubscribeData = [];
        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            unsubscribeData.forEach((unsubscribe) => unsubscribe());
            unsubscribeData = [];
            setUser(null);
            setRecords({});
            setErrors({});
            setLoadingCollections(true);
            setCheckingAuth(true);
            if (!currentUser) {
                setCheckingAuth(false);
                navigate("/signin", { replace: true });
                return;
            }
            try {
                // Admin access is controlled by users/{uid}.role in Firestore.
                const profileSnapshot = await getDoc(doc(db, "users", currentUser.uid));
                const profile = profileSnapshot.exists() ? profileSnapshot.data() : null;
                if (profile?.role !== "admin") {
                    setAuthError("This account does not have administrator access. Set this user's role to admin in Firestore.");
                    setCheckingAuth(false);
                    await signOut(auth);
                    navigate("/signin", { replace: true });
                    return;
                }
                setUser(currentUser);
                // Subscribe to user profiles only after admin access is verified.
                // The Firestore rules allow this collection read to admins only.
                const usersUnsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
                    setRecords((previous) => ({
                        ...previous,
                        users: snapshot.docs.map((item) => ({
                            id: item.id,
                            ...item.data(),
                        })),
                    }));
                    setErrors((previous) => ({ ...previous, users: "" }));
                    setLoadingCollections(false);
                }, (error) => {
                    console.error("Failed to load users:", error);
                    setErrors((previous) => ({
                        ...previous,
                        users: "Could not load user profiles. Check Firestore read rules.",
                    }));
                    setLoadingCollections(false);
                });
                unsubscribeData.push(usersUnsubscribe);
                sections
                    .filter((section) => section.collection)
                    .forEach((section) => {
                    const unsubscribe = onSnapshot(collection(db, section.collection), (snapshot) => {
                        setRecords((previous) => ({
                            ...previous,
                            [section.key]: snapshot.docs.map((item) => ({
                                id: item.id,
                                ...item.data(),
                            })),
                        }));
                        setErrors((previous) => ({
                            ...previous,
                            [section.key]: "",
                        }));
                        setLoadingCollections(false);
                    }, (error) => {
                        console.error(`Failed to load ${section.collection}:`, error);
                        setErrors((previous) => ({
                            ...previous,
                            [section.key]: "Could not load this collection. Check its name and Firestore security rules.",
                        }));
                        setLoadingCollections(false);
                    });
                    unsubscribeData.push(unsubscribe);
                });
                setCheckingAuth(false);
            }
            catch (error) {
                console.error("Admin authorization check failed:", error);
                setAuthError("Unable to verify admin access. Please sign in again.");
                setCheckingAuth(false);
                await signOut(auth);
                navigate("/signin", { replace: true });
            }
        });
        return () => {
            unsubscribeAuth();
            unsubscribeData.forEach((unsubscribe) => unsubscribe());
        };
    }, [navigate]);
    const handleEnquiryReply = async (record) => {
        const reply = String(enquiryReplies[record.id] ?? record.adminReply ?? "").trim();
        if (!reply) { window.alert("Please enter a reply first."); return; }
        setSavingEnquiryId(record.id);
        try {
            await updateDoc(doc(db, "enquiries", record.id), { adminReply: reply, status: "answered", repliedAt: serverTimestamp(), repliedBy: user?.uid || "", updatedAt: serverTimestamp() });
            setEnquiryReplies((previous) => ({ ...previous, [record.id]: reply }));
        } catch (error) {
            console.error("Enquiry reply failed:", error);
            window.alert("Could not save reply. Please check Firestore rules and try again.");
        } finally { setSavingEnquiryId(""); }
    };
    const currentSection = sections.find((item) => item.key === active);
    const allOrders = records.orders || [];
    const allResellers = records.resellers || [];
    const allShopOwners = records.shopOwners || [];
    const allPayments = records.payments || [];
    const allSettlements = records.settlements || [];
    const allEnquiries = records.enquiries || [];
    const allUsers = records.users || [];
    const receivedPaymentRows = [
        ...allPayments.map((item) => ({ ...item, sourceCollection: "payments", paymentDirection: "in", displayPaymentType: item.type || item.category || "Payment received" })),
        ...allOrders.filter((item) => Number(item.customerPaid ?? item.totalAmount ?? item.amount ?? 0) > 0 && !/failed|cancelled|refunded/.test(String(item.paymentStatus || item.status || "").toLowerCase())).map((item) => ({ ...item, id: `order-${item.id}`, sourceId: item.id, sourceCollection: "orders", paymentDirection: "in", displayPaymentType: "Customer order payment", amount: Number(item.customerPaid ?? item.totalAmount ?? item.amount ?? 0), status: item.paymentStatus || item.status || "received" })),
        ...allResellers.filter((item) => /verified|paid|success|complete/.test(String(item.paymentStatus || "").toLowerCase())).map((item) => ({ ...item, id: `registration-${item.id}`, sourceId: item.id, sourceCollection: "resellerApplications", paymentDirection: "in", displayPaymentType: "Reseller registration fee", amount: Number(item.setupFee ?? item.registrationFee ?? item.amount ?? 0), status: item.paymentStatus })),
        ...allSettlements.filter((item) => /paid/.test(String(item.status || "").toLowerCase())).map((item) => ({ ...item, id: `payout-${item.id}`, sourceId: item.id, sourceCollection: item.sourceCollection || "withdrawals", paymentDirection: "out", displayPaymentType: "Settlement paid", amount: -Math.abs(Number(item.amount ?? item.requestedAmount ?? 0)), status: "paid" })),
    ];
    const walletActionRows = allSettlements.filter((item) => !/paid|rejected|cancelled/.test(String(item.status || "").toLowerCase()));
    const filteredRecords = useMemo(() => {
        const currentRecords = active === "payments" ? receivedPaymentRows : active === "wallets" ? walletActionRows : (records[active] || []);
        const query = search.trim().toLowerCase();
        if (!query) return currentRecords;
        return currentRecords.filter((record) => Object.entries(record).some(([key, value]) => {
            if (/screenshot(data|url|path)/i.test(key)) return false;
            return String(value ?? "").toLowerCase().includes(query);
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [records, active, search, receivedPaymentRows.length, walletActionRows.length]);
    const relatedUser = (record) => {
        const uid = record?.uid ||
            record?.userId ||
            record?.ownerUid ||
            record?.resellerId ||
            record?.shopOwnerId ||
            record?.customerId ||
            record?.ownerId ||
            record?.id;
        return allUsers.find((profile) => profile.id === uid ||
            profile.uid === uid ||
            profile.userId === uid);
    };
    const personName = (record) => {
        const profile = relatedUser(record);
        const directName = record?.fullName ||
            record?.name ||
            record?.displayName ||
            record?.ownerName ||
            record?.customerName ||
            record?.resellerName;
        const profileName = profile?.fullName ||
            profile?.name ||
            profile?.displayName ||
            profile?.ownerName ||
            profile?.customerName ||
            profile?.resellerName;
        return directName || profileName || "Name not saved";
    };
    const pendingWithdrawals = allSettlements.filter((item) => /pending|requested|waiting/.test(String(valueOf(item, ["status", "state"], "")).toLowerCase()));
    const today = new Date();
    const todayOrders = allOrders.filter((item) => {
        const raw = item.createdAt || item.orderDate || item.date;
        if (!raw)
            return false;
        const date = raw?.toDate ? raw.toDate() : new Date(raw);
        return !Number.isNaN(date.getTime()) &&
            date.toDateString() === today.toDateString();
    });
    const totalSales = allOrders.reduce((sum, item) => {
        const status = String(valueOf(item, ["status", "orderStatus"], "")).toLowerCase();
        if (/cancel|refund|failed/.test(status))
            return sum;
        return sum + Number(valueOf(item, ["totalAmount", "sellingPrice", "amount", "total"], 0));
    }, 0);
    const verifiedApplications = allResellers.filter((item) => /verified|paid|success|complete/.test(String(item.paymentStatus || "").toLowerCase()));
    const verifiedApplicationUids = new Set(verifiedApplications.flatMap((item) => [item.uid, item.userId, item.id].filter(Boolean)));
    const setupFeesFromApplications = verifiedApplications.reduce((sum, item) => sum + Number(valueOf(item, ["setupFee", "registrationFee", "amount", "totalAmount"], 0)), 0);
    const setupFeesFromPayments = allPayments.reduce((sum, item) => {
        const type = String(valueOf(item, ["type", "category", "purpose"], "")).toLowerCase();
        const status = String(valueOf(item, ["status", "paymentStatus"], "")).toLowerCase();
        const relatedUid = item.uid || item.userId || item.resellerId || item.ownerUid;
        // Avoid counting the same reseller registration in both collections.
        if (/setup|registration/.test(type) && /paid|verified|success|complete/.test(status) && !verifiedApplicationUids.has(relatedUid)) {
            return sum + Number(valueOf(item, ["amount", "total", "totalAmount"], 0));
        }
        return sum;
    }, 0);
    const setupFees = setupFeesFromApplications + setupFeesFromPayments;
    const walletRecords = records.wallets || [];
    const totalWallets = walletRecords.length
        ? walletRecords.reduce((sum, item) => sum + Number(valueOf(item, ["balance", "availableBalance", "walletBalance"], 0)), 0)
        : allUsers.reduce((sum, item) => sum + Number(item.walletBalance ?? item.wallet?.balance ?? item.availableBalance ?? 0), 0);
    const goTo = (key) => {
        setActive(key);
        setSearch("");
        setMobileOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleApplicationDecision = async (record, applicationType, nextStatus) => {
        const collectionName = applicationType === "reseller" ? "resellerApplications" : "shopOwnerApplications";
        const uid = record.uid || record.userId || record.id;
        if (!uid) {
            setAuthError("Application is missing a user ID; no changes were made.");
            return;
        }
        try {
            await updateDoc(doc(db, collectionName, record.id), {
                applicationStatus: nextStatus,
                reviewedAt: serverTimestamp(),
                reviewedBy: user.uid,
            });
            // Keep the user profile in sync with the application decision.
            await updateDoc(doc(db, "users", uid), {
                status: nextStatus === "approved" ? "approved" : "rejected",
                applicationStatus: nextStatus,
                reviewedAt: serverTimestamp(),
                reviewedBy: user.uid,
            });
            // Create or sync the public store after an admin approves/rejects a reseller.
            // publicStores writes remain admin-only in Firestore rules.
            if (applicationType === "reseller") {
                const profileSnapshot = await getDoc(doc(db, "users", uid));
                const profileData = profileSnapshot.exists() ? profileSnapshot.data() : null;
                const rawSlug = profileData?.storeSlug || profileData?.slug || profileData?.store?.slug;
                const slug = String(rawSlug || "").trim().toLowerCase();
                if (!profileData) {
                    throw new Error(`User profile not found for reseller ${uid}.`);
                }
                if (!slug) {
                    throw new Error(`No storeSlug found in users/${uid}; cannot sync public store.`);
                }
                const publicStoreRef = doc(db, "publicStores", slug);
                const publicStoreSnapshot = await getDoc(publicStoreRef);
                const existingStore = publicStoreSnapshot.exists() ? publicStoreSnapshot.data() : null;
                if (existingStore && existingStore.ownerUid !== uid && existingStore.resellerId !== uid) {
                    throw new Error(`Store slug "${slug}" is already linked to another account.`);
                }
                if (nextStatus === "approved") {
                    const storePayload = {
                        ownerUid: uid,
                        resellerId: uid,
                        storeSlug: slug,
                        storeName: profileData.storeName || profileData.shopName || profileData.name || "Saree Boutique",
                        tagline: profileData.tagline || profileData.storeDescription || "Discover sarees for every occasion.",
                        status: "approved",
                        updatedAt: serverTimestamp(),
                        reviewedAt: serverTimestamp(),
                        reviewedBy: user.uid,
                    };
                    if (!existingStore)
                        storePayload.createdAt = serverTimestamp();
                    await setDoc(publicStoreRef, storePayload, { merge: true });
                }
                else if (existingStore) {
                    await updateDoc(publicStoreRef, {
                        status: "rejected",
                        updatedAt: serverTimestamp(),
                        reviewedAt: serverTimestamp(),
                        reviewedBy: user.uid,
                    });
                }
            }
        }
        catch (error) {
            console.error("Could not update application:", error);
            setAuthError("Application update failed. Check Firestore rules and confirm the user profile exists.");
        }
    };
    const handlePaymentVerification = async (record, nextStatus) => {
        const uid = record.uid || record.userId || record.id;
        if (!record.id)
            return;
        const confirmed = window.confirm(nextStatus === "verified"
            ? `Mark ${personName(record)}'s registration payment as verified?`
            : `Mark ${personName(record)}'s registration payment as rejected?`);
        if (!confirmed)
            return;
        try {
            await updateDoc(doc(db, "resellerApplications", record.id), {
                paymentStatus: nextStatus,
                paymentVerifiedAt: serverTimestamp(),
                paymentVerifiedBy: user.uid,
            });
            if (uid) {
                const profileRef = doc(db, "users", uid);
                const profileSnapshot = await getDoc(profileRef);
                if (profileSnapshot.exists()) {
                    await updateDoc(profileRef, {
                        paymentStatus: nextStatus,
                        paymentVerifiedAt: serverTimestamp(),
                        paymentVerifiedBy: user.uid,
                    });
                }
            }
            setAuthError(nextStatus === "verified" ? "Registration payment marked verified." : "Registration payment marked rejected.");
        }
        catch (error) {
            console.error("Registration payment verification failed:", error);
            setAuthError("Could not update registration payment. Check admin Firestore update rules.");
        }
    };
    const handleOrderStatus = async (record, nextStatus) => {
        const confirmed = window.confirm(`Change order status to ${nextStatus.replaceAll("_", " ")}?`);
        if (!confirmed)
            return;
        try {
            await updateDoc(doc(db, "orders", record.id), {
                status: nextStatus,
                orderStatus: nextStatus,
                updatedAt: serverTimestamp(),
                updatedBy: user.uid,
            });
            setAuthError(`Order status updated to ${nextStatus.replaceAll("_", " ")}.`);
        }
        catch (error) {
            console.error("Order status update failed:", error);
            setAuthError("Could not update order status. Check admin Firestore update rules.");
        }
    };
    const handleSettlementDecision = async (record, nextStatus) => {
        const currentStatus = String(record.status || "pending").toLowerCase();
        if (nextStatus === "paid" && currentStatus === "paid") {
            setAuthError("This withdrawal is already marked paid; no duplicate wallet deduction was made.");
            return;
        }
        try {
            await updateDoc(doc(db, record.sourceCollection || "withdrawals", record.sourceId || record.id), {
                status: nextStatus,
                reviewedAt: serverTimestamp(),
                reviewedBy: user.uid,
            });
            // A payout is only recorded in the profile after an admin marks it paid.
            // This updates the app ledger; it does not itself transfer money through a bank/UPI.
            if (nextStatus === "paid" && !/paid/.test(currentStatus)) {
                const uid = record.shopOwnerId || record.resellerId || record.userId || record.uid;
                const amount = Number(record.amount ?? record.requestedAmount ?? 0);
                if (uid && amount > 0) {
                    const profileRef = doc(db, "users", uid);
                    const profileSnap = await getDoc(profileRef);
                    if (profileSnap.exists()) {
                        const profileData = profileSnap.data();
                        const currentBalance = Number(profileData.walletBalance ?? profileData.wallet?.balance ?? profileData.availableBalance ?? 0);
                        const nextBalance = Math.max(0, currentBalance - amount);
                        await updateDoc(profileRef, {
                            walletBalance: nextBalance,
                            availableBalance: nextBalance,
                            "wallet.balance": nextBalance,
                            updatedAt: serverTimestamp(),
                        });
                    }
                }
            }
            setAuthError(`Settlement marked ${nextStatus}.`);
        }
        catch (error) {
            console.error("Settlement update failed:", error);
            setAuthError("Could not update settlement/wallet. Check admin Firestore update rules and user profile fields.");
        }
    };
    const confirmSettlementPaid = async (event) => {
        event.preventDefault();
        if (!settlementModal || !utrNumber.trim()) { setAuthError("Enter the UTR / transaction reference number."); return; }
        const record = settlementModal;
        const currentStatus = String(record.status || "pending").toLowerCase();
        if (currentStatus === "paid") { setSettlementModal(null); return; }
        setSettlementSaving(true);
        try {
            const collectionName = record.sourceCollection || "withdrawals";
            const recordId = record.sourceId || record.id;
            await updateDoc(doc(db, collectionName, recordId), {
                status: "paid", utrNumber: utrNumber.trim(), transactionReference: utrNumber.trim(),
                paidAt: serverTimestamp(), reviewedAt: serverTimestamp(), reviewedBy: user.uid,
            });
            const uid = record.shopOwnerId || record.resellerId || record.userId || record.uid;
            const amount = Number(record.amount ?? record.requestedAmount ?? 0);
            if (uid && amount > 0) {
                const profileRef = doc(db, "users", uid);
                const profileSnap = await getDoc(profileRef);
                if (profileSnap.exists()) {
                    const profileData = profileSnap.data();
                    const currentBalance = Number(profileData.walletBalance ?? profileData.wallet?.balance ?? profileData.availableBalance ?? 0);
                    const nextBalance = Math.max(0, currentBalance - amount);
                    await updateDoc(profileRef, { walletBalance: nextBalance, availableBalance: nextBalance, "wallet.balance": nextBalance, updatedAt: serverTimestamp() });
                }
            }
            setAuthError(`Marked paid. UTR ${utrNumber.trim()} saved. Actual bank/UPI transfer must be completed separately.`);
            setSettlementModal(null); setUtrNumber("");
        } catch (error) {
            console.error("Payout recording failed:", error);
            setAuthError("Could not record payout. Check Firestore rules and try again.");
        } finally { setSettlementSaving(false); }
    };
    const handleLogout = async () => {
        setSigningOut(true);
        try {
            await signOut(auth);
            navigate("/signin", { replace: true });
        }
        catch (error) {
            console.error("Logout failed:", error);
            setAuthError("Could not sign out. Please try again.");
            setSigningOut(false);
        }
    };
    if (checkingAuth) {
        return (<div className="flex min-h-screen items-center justify-center bg-[#faf7f5]">
        <div className="text-center">
          <RefreshCw className="mx-auto animate-spin text-rose-900" size={30}/>
          <p className="mt-3 text-sm text-stone-600">Verifying administrator access...</p>
        </div>
      </div>);
    }
    const sidebar = (<>
      <div className="flex items-center gap-3 px-5 py-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-300 text-rose-950">
          <span className="text-xl">♥</span>
        </div>
        <div>
          <p className="font-bold text-white">I Love Shopping</p>
          <p className="text-xs text-rose-200">Admin workspace</p>
        </div>
      </div>

      <div className="border-y border-white/10 px-5 py-4">
        <p className="text-xs text-rose-200">Signed in as</p>
        <p className="mt-1 truncate text-sm font-bold text-amber-200">
          {user?.displayName || "Main Admin"}
        </p>
        <p className="mt-1 truncate text-xs text-rose-200">{user?.email}</p>
      </div>

      <nav className="flex-1 space-y-1 py-3">
        {sections.map(({ label, icon: Icon, key }) => (<button key={key} onClick={() => goTo(key)} className={`flex w-full items-center gap-3 border-l-[3px] px-4 py-3 text-left text-sm transition ${active === key
                ? "border-amber-300 bg-[#590f29] font-bold text-white"
                : "border-transparent text-rose-100 hover:bg-white/10"}`}>
            <Icon size={17}/>
            <span>{label}</span>
            {key === "settlements" && pendingWithdrawals.length > 0 && (<span className="ml-auto rounded-full bg-amber-300 px-2 py-0.5 text-[10px] font-bold text-rose-950">
                {pendingWithdrawals.length}
              </span>)}
          </button>))}

      </nav>

      <div className="p-3">
        <button onClick={handleLogout} disabled={signingOut} className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-rose-100 hover:bg-white/10 disabled:opacity-50">
          <LogOut size={17}/> {signingOut ? "Logging out..." : "Log out"}
        </button>
      </div>
    </>);
    return (<div className="min-h-screen overflow-x-hidden bg-[#faf7f5] text-stone-800">
      {mobileOpen && (<div className="fixed inset-0 z-50 lg:hidden">
          <button aria-label="Close menu" onClick={() => setMobileOpen(false)} className="absolute inset-0 bg-black/50"/>
          <aside className="relative flex h-full w-[min(270px,88vw)] flex-col overflow-y-auto bg-[#68132f]">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-3 rounded-lg p-2 text-white" aria-label="Close sidebar">
              <X size={20}/>
            </button>
            {sidebar}
          </aside>
        </div>)}

      <div className="flex min-h-screen">
        <aside className="hidden w-[252px] shrink-0 flex-col bg-[#68132f] lg:flex">
          {sidebar}
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 flex min-h-[60px] items-center justify-between gap-2 border-b border-stone-200 bg-white px-3 py-2 shadow-sm sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <button onClick={() => setMobileOpen(true)} aria-label="Open menu" className="rounded-lg border border-stone-200 p-2 lg:hidden">
                <Menu size={19}/>
              </button>
              <h1 className="truncate text-base font-semibold text-rose-950">
                {currentSection?.label || "Dashboard"}
              </h1>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 sm:flex">
                <Wallet size={15}/> {money(totalWallets)} in wallets
              </div>
              <div title="Admin access is verified using a Firestore user profile" className="hidden items-center gap-1.5 text-xs font-semibold text-rose-900 md:flex">
                <ShieldCheck size={17}/> Main Admin
              </div>
              <button onClick={() => setAuthError("Live records update automatically when Firestore changes.")} aria-label="Notifications" className="rounded-lg border border-stone-200 p-2.5 text-rose-900 hover:bg-stone-50">
                <Bell size={17}/>
              </button>
              <button onClick={handleLogout} disabled={signingOut} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-600 hover:bg-stone-50">
                <LogOut size={14}/>
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </header>

          <main className="mx-auto min-w-0 max-w-[1800px] p-3 sm:p-5 lg:p-6">
            {authError && (<div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertCircle className="mt-0.5 shrink-0" size={17}/>
                <span>{authError}</span>
                <button onClick={() => setAuthError("")} className="ml-auto" aria-label="Dismiss">
                  <X size={17}/>
                </button>
              </div>)}

            {active === "dashboard" ? (<>
                <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-rose-950">Dashboard</h2>
                    <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
                      Live platform overview from Firestore. Financial totals depend on the fields and transaction records configured in your database.
                    </p>
                  </div>
                  <button onClick={() => window.location.reload()} className="inline-flex w-fit items-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold hover:bg-stone-50">
                    <RefreshCw size={15}/> Refresh
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                  <StatCard icon={ShoppingBag} label="Today's orders" value={todayOrders.length} note="Created today"/>
                  <StatCard icon={Clock} label="Withdrawals to review" value={pendingWithdrawals.length} note="Pending requests" tone="red"/>
                  <StatCard icon={Wallet} label="Wallet balances" value={money(totalWallets)} note="From wallet records" tone="gold"/>
                  <StatCard icon={TrendingUp} label="Recorded sales" value={money(totalSales)} note="Non-cancelled orders" tone="green"/>
                  <StatCard icon={CreditCard} label="Setup fees received" value={money(setupFees)} note="Recorded successful setup payments" tone="purple"/>
                  <StatCard icon={Users} label="Resellers" value={allResellers.length} note="Reseller records" tone="blue"/>
                  <StatCard icon={Store} label="Shop owners" value={allShopOwners.length} note="Shop owner records" tone="blue"/>
                  <StatCard icon={MessageSquare} label="New enquiries" value={allEnquiries.filter((item) => /new|open|pending/.test(String(item.status || "new").toLowerCase())).length} note="Awaiting a response" tone="red"/>
                </div>

                <div className="mt-6 grid gap-5 xl:grid-cols-2">
                  <section className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                    <div className="flex items-center justify-between border-b border-stone-200 p-4">
                      <h3 className="flex items-center gap-2 font-semibold text-stone-900">
                        <Package size={17} className="text-rose-800"/> Latest orders
                      </h3>
                      <button onClick={() => goTo("orders")} className="text-xs font-semibold text-rose-800">All orders →</button>
                    </div>
                    {allOrders.length === 0 ? (<EmptyState message="No order records found in Firestore yet."/>) : (<div className="divide-y divide-stone-100">
                        {allOrders.slice(0, 6).map((order) => (<div key={order.id} className="flex items-center gap-3 p-4">
                            <div className="rounded-full bg-rose-50 p-2.5 text-rose-800"><ShoppingBag size={17}/></div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-stone-900">
                                {valueOf(order, ["orderNumber", "orderId", "id"])}
                                {" · "}
                                {valueOf(order, ["customerName", "customer", "name"])}
                              </p>
                              <p className="mt-1 truncate text-xs text-stone-500">
                                {displayDate(order.createdAt || order.orderDate || order.date)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="mb-2 text-sm font-semibold">{money(valueOf(order, ["totalAmount", "sellingPrice", "amount"], 0))}</p>
                              <Status value={valueOf(order, ["status", "orderStatus"])}/>
                            </div>
                          </div>))}
                      </div>)}
                  </section>

                  <section className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                    <div className="flex items-center justify-between border-b border-stone-200 p-4">
                      <h3 className="flex items-center gap-2 font-semibold text-stone-900">
                        <Landmark size={17} className="text-rose-800"/> Pending settlements
                      </h3>
                      <button onClick={() => goTo("settlements")} className="text-xs font-semibold text-rose-800">Review →</button>
                    </div>
                    {pendingWithdrawals.length === 0 ? (<EmptyState message="No pending settlement records found."/>) : (<div className="divide-y divide-stone-100">
                        {pendingWithdrawals.slice(0, 6).map((item) => (<div key={item.id} className="flex items-center gap-3 p-4">
                            <div className="rounded-full bg-amber-50 p-2.5 text-amber-800"><Clock size={17}/></div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold">{valueOf(item, ["name", "accountName", "ownerName", "uid"])}</p>
                              <p className="mt-1 text-xs text-stone-500">{valueOf(item, ["upiId", "upi", "paymentMethod"])}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold">{money(valueOf(item, ["amount", "requestedAmount"], 0))}</p>
                              <Status value={valueOf(item, ["status"], "Pending")}/>
                            </div>
                          </div>))}
                      </div>)}
                  </section>
                </div>
              </>) : active === "website" ? (<section className="rounded-xl border border-stone-200 bg-white p-6">
                <h2 className="text-2xl font-bold text-rose-950">Landing page</h2>
                <p className="mt-2 text-sm leading-6 text-stone-500">
                  Your public landing page remains the existing website. Website editing controls can be added after the content and publishing workflow are defined.
                </p>
                <button onClick={() => navigate("/")} className="mt-5 rounded-lg bg-rose-900 px-4 py-2.5 text-sm font-semibold text-white">
                  Open website
                </button>
              </section>) : (<>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-rose-950">{currentSection?.label}</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Live records loaded from the configured Firestore collection.
                  </p>
                </div>

                <div className="mb-4 flex flex-col gap-3 sm:flex-row">
                  <div className="relative max-w-xl flex-1">
                    <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                    <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${currentSection?.label?.toLowerCase() || "records"}...`} className="w-full rounded-xl border border-stone-200 bg-white py-3 pl-10 pr-4 text-sm outline-none focus:border-rose-800"/>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-stone-500">
                    <RefreshCw size={14} className={loadingCollections ? "animate-spin" : ""}/>
                    {loadingCollections ? "Loading records..." : `${filteredRecords.length} records`}
                  </div>
                </div>

                {errors[active] ? (<div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm leading-6 text-red-800">
                    <AlertCircle className="mb-2" size={20}/>
                    <p className="font-semibold">{errors[active]}</p>
                    <p className="mt-2 text-xs">
                      Expected collection: <code>{currentSection?.collection}</code>. Verify the actual collection name and the admin Firestore read rules.
                    </p>
                  </div>) : filteredRecords.length === 0 ? (<EmptyState message={loadingCollections ? "Loading live records..." : "No records found. This section will show records once the corresponding Firestore collection contains data."}/>) : (<div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[760px] text-left text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500">
                          <tr>
                            <th className="px-4 py-3 font-medium">Record</th>
                            <th className="px-4 py-3 font-medium">Details</th>
                            <th className="px-4 py-3 font-medium">Amount</th>
                            <th className="px-4 py-3 font-medium">Date</th>
                            <th className="px-4 py-3 font-medium">Status</th>
                            {(active === "resellers" || active === "shopOwners" || active === "orders") && <th className="px-4 py-3 font-medium">Payment proof</th>}
                            {(active === "resellers" || active === "shopOwners") && <th className="px-4 py-3 font-medium">Review</th>}
                            {(active === "orders" || active === "settlements" || active === "wallets") && <th className="px-4 py-3 font-medium">Actions</th>}
                            <th className="px-4 py-3 font-medium">Document ID</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                          {filteredRecords.map((record) => (<tr key={record.id} className="hover:bg-stone-50">
                              <td className="px-4 py-4 font-semibold text-stone-900">
                                {active === "shopOwners" || active === "resellers"
                        ? personName(record)
                        : active === "payments" ? (record.displayPaymentType || "Payment") : active === "wallets" ? (record.shopName || record.ownerName || record.resellerName || personName(record)) : valueOf(record, [
                            "orderNumber",
                            "orderId",
                            "name",
                            "shopName",
                            "storeName",
                            "title",
                            "customerName",
                            "email",
                        ], record.id)}
                              </td>
                              <td className="max-w-xs px-4 py-4 text-stone-600">
                                {active === "enquiries" ? <div className="min-w-[260px] space-y-2"><p className="font-semibold text-stone-800">{record.subject || "Support enquiry"}</p><p className="whitespace-pre-wrap text-stone-600">{record.message || record.description || "No message provided."}</p><p className="text-xs text-stone-400">{record.accountType || "Account"} · {record.email || "No email"}</p>{record.adminReply && <div className="rounded-lg bg-emerald-50 p-3"><p className="text-xs font-bold text-emerald-800">Your reply</p><p className="mt-1 whitespace-pre-wrap text-sm text-emerald-950">{record.adminReply}</p></div>}<textarea rows={2} value={enquiryReplies[record.id] ?? record.adminReply ?? ""} onChange={(e) => setEnquiryReplies((previous) => ({ ...previous, [record.id]: e.target.value }))} placeholder="Write a reply to this enquiry..." className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"/><button type="button" disabled={savingEnquiryId === record.id} onClick={() => handleEnquiryReply(record)} className="rounded-lg bg-rose-900 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">{savingEnquiryId === record.id ? "Saving..." : "Save reply"}</button></div> : valueOf(record, ["shopName", "storeName", "email", "mobile", "phone", "description", "productName", "subject", "upiId", "utrNumber", "transactionReference"], valueOf(relatedUser(record), ["email", "mobile", "phone"], "—"))}
                              </td>
                              <td className="whitespace-nowrap px-4 py-4 font-semibold">
                                {active === "payments" ? (record.paymentDirection === "out" ? `−${money(Math.abs(Number(record.amount ?? record.totalAmount ?? record.customerPaid ?? 0)))}` : /paid|verified|success|received|complete/.test(String(record.status || record.paymentStatus || "").toLowerCase()) ? `+${money(Math.abs(Number(record.amount ?? record.totalAmount ?? record.customerPaid ?? 0)))}` : `Pending · ${money(Math.abs(Number(record.amount ?? record.totalAmount ?? record.customerPaid ?? 0)))}`) : money(valueOf(record, active === "resellers" ? ["setupFee", "registrationFee", "amount", "totalAmount"] : ["shopOwnerSubtotal", "totalAmount", "total", "amount", "sellingPrice", "requestedAmount", "balance", "availableBalance"], 0))}
                              </td>
                              <td className="whitespace-nowrap px-4 py-4 text-stone-500">
                                {displayDate(record.createdAt || record.date || record.orderDate || record.requestedAt)}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex flex-col items-start gap-1.5">
                                  <Status value={valueOf(record, active === "resellers" ? ["applicationStatus", "status"] : ["orderStatus", "status", "applicationStatus"])}/>
                                  {active === "resellers" && <span className="text-xs text-stone-500">Fee: {String(record.paymentStatus || "not submitted").replaceAll("_", " ")}</span>}
                                  {active === "orders" && <span className="text-xs text-stone-500">Payment: {String(record.paymentStatus || "—").replaceAll("_", " ")}</span>}
                                </div>
                              </td>
                              {(active === "resellers" || active === "shopOwners" || active === "orders") && (<td className="px-4 py-4">
                                  {record.paymentScreenshotData || record.paymentScreenshotURL || record.paymentScreenshotUrl ? (<div className="flex min-w-[150px] flex-col items-start gap-2">
                                      <div className="flex flex-wrap items-center gap-3">
                                        <a href={record.paymentScreenshotData || record.paymentScreenshotURL || record.paymentScreenshotUrl} target="_blank" rel="noreferrer" className="font-semibold text-rose-800 underline">
                                          View screenshot
                                        </a>
                                        <a href={record.paymentScreenshotData || record.paymentScreenshotURL || record.paymentScreenshotUrl} download={`payment-proof-${record.id}.jpg`} target="_blank" rel="noreferrer" className="rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-semibold text-rose-900 hover:bg-rose-50">
                                          Download screenshot
                                        </a>
                                      </div>
                                      {(record.paymentScreenshotData || record.paymentScreenshotURL || record.paymentScreenshotUrl) && (<a href={record.paymentScreenshotData || record.paymentScreenshotURL || record.paymentScreenshotUrl} target="_blank" rel="noreferrer" aria-label="Open payment screenshot in a new tab">
                                          <img src={record.paymentScreenshotData || record.paymentScreenshotURL || record.paymentScreenshotUrl} alt="Payment screenshot preview" loading="lazy" className="h-20 w-20 rounded-lg border border-stone-200 bg-stone-50 object-contain"/>
                                        </a>)}
                                    </div>) : (<span className="text-xs text-stone-400">No screenshot</span>)}
                                </td>)}
                              {(active === "resellers" || active === "shopOwners") && (<td className="px-4 py-4">
                                  <div className="flex min-w-[170px] flex-col items-start gap-2">
                                    {active === "resellers" && !/verified|paid|success|complete/.test(String(record.paymentStatus || "").toLowerCase()) && (<div className="flex gap-2">
                                        <button type="button" onClick={() => handlePaymentVerification(record, "verified")} className="rounded-lg bg-blue-100 px-3 py-1.5 text-xs font-bold text-blue-800 hover:bg-blue-200">Verify fee</button>
                                        <button type="button" onClick={() => handlePaymentVerification(record, "rejected")} className="rounded-lg bg-orange-100 px-3 py-1.5 text-xs font-bold text-orange-800 hover:bg-orange-200">Reject fee</button>
                                      </div>)}
                                    <div className="flex gap-2">
                                      <button type="button" onClick={() => handleApplicationDecision(record, active === "resellers" ? "reseller" : "shopOwner", "approved")} className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-200">Approve</button>
                                      <button type="button" onClick={() => handleApplicationDecision(record, active === "resellers" ? "reseller" : "shopOwner", "rejected")} className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-bold text-red-800 hover:bg-red-200">Reject</button>
                                    </div>
                                  </div>
                                </td>)}
                              {(active === "orders" || active === "settlements" || active === "wallets") && (<td className="px-4 py-4">
                                  {active === "orders" ? (<div className="flex min-w-[190px] flex-wrap gap-2">
                                      {["confirmed", "processing", "shipped", "delivered", "cancelled"].map((nextStatus) => (<button key={nextStatus} type="button" onClick={() => handleOrderStatus(record, nextStatus)} className="rounded-lg border border-stone-200 px-2 py-1.5 text-xs font-semibold capitalize hover:border-rose-300 hover:bg-rose-50">{nextStatus}</button>))}
                                    </div>) : (<div className="flex min-w-[150px] flex-wrap gap-2">
                                      <button type="button" onClick={() => handleSettlementDecision(record, "approved")} className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-200">Approve</button>
                                      <button type="button" onClick={() => handleSettlementDecision(record, "rejected")} className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-bold text-red-800 hover:bg-red-200">Reject</button>
                                      <button type="button" onClick={() => { setSettlementModal(record); setUtrNumber(""); }} className="rounded-lg bg-blue-100 px-3 py-1.5 text-xs font-bold text-blue-800 hover:bg-blue-200">Mark paid</button>
                                    </div>)}
                                </td>)}
                              <td className="px-4 py-4">
                                <code className="rounded bg-slate-100 px-2 py-1 text-xs">{record.id}</code>
                              </td>
                            </tr>))}
                        </tbody>
                      </table>
                    </div>
                  </div>)}
              </>)}
            {settlementModal && (<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
              <form onSubmit={confirmSettlementPaid} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
                <div className="flex items-start justify-between gap-3">
                  <div><h2 className="text-xl font-bold text-rose-950">Confirm payout</h2><p className="mt-1 text-sm text-stone-500">Verify recipient and save the transaction reference.</p></div>
                  <button type="button" onClick={() => setSettlementModal(null)} className="rounded-lg p-2 hover:bg-stone-100" aria-label="Close payout form"><X size={18}/></button>
                </div>
                <div className="mt-5 grid gap-3 rounded-xl bg-stone-50 p-4 text-sm">
                  <div className="flex justify-between gap-3"><span className="text-stone-500">Recipient</span><strong className="text-right">{settlementModal.shopName || settlementModal.ownerName || settlementModal.resellerName || personName(settlementModal)}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-stone-500">Account type</span><strong>{settlementModal.shopOwnerId ? "Shop Owner" : settlementModal.resellerId ? "Reseller" : "Wallet recipient"}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-stone-500">UPI ID</span><strong className="break-all text-right">{settlementModal.upiId || "UPI ID not saved"}</strong></div>
                  <div className="flex justify-between gap-3"><span className="text-stone-500">Amount to pay</span><strong className="text-right">{money(settlementModal.amount ?? settlementModal.requestedAmount ?? 0)}</strong></div>
                </div>
                <label className="mt-5 block text-sm font-semibold text-stone-700">UTR / Transaction number
                  <input value={utrNumber} onChange={(e) => setUtrNumber(e.target.value)} required maxLength={100} placeholder="Enter bank/UPI transaction reference" className="mt-2 w-full rounded-xl border border-stone-300 px-4 py-3 font-normal outline-none focus:border-rose-700" />
                </label>
                <p className="mt-3 text-xs leading-5 text-amber-800">Only mark paid after you have actually transferred the money. This form records the payout; it does not send a payment.</p>
                <div className="mt-5 flex justify-end gap-3"><button type="button" onClick={() => setSettlementModal(null)} className="rounded-xl border px-4 py-2.5 text-sm font-semibold">Cancel</button><button disabled={settlementSaving} className="rounded-xl bg-rose-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{settlementSaving ? "Saving..." : "Confirm paid"}</button></div>
              </form>
            </div>)}
          </main>
        </div>
      </div>
    </div>);
}
function EmptyState({ message }) {
    return (<div className="flex min-h-44 flex-col items-center justify-center p-8 text-center">
      <div className="rounded-full bg-rose-50 p-3 text-rose-800">
        <Package size={24}/>
      </div>
      <p className="mt-3 max-w-md text-sm leading-6 text-stone-500">{message}</p>
    </div>);
}
